import React, { useRef, useCallback } from "react";
import { Box } from "@mui/material";

import VideoEditorCurrentTime from "./components/VideoEditorCurrentTime";
import VideoEditorTimeline from "./components/VideoEditorTimeline";
import VideoEditorToolbar from "./components/VideoEditorToolbar";

// 1. Import the Autonomous Brain
import { useKernel } from "../../hooks/AIKernel"; // Adjust the relative path to where your AIKernel is located

const VideoEditor: React.FC = () => {
  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  // 3. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const bgColor = isDarkMode ? "#121212" : "#ffffff";
  const borderColor = isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)";
  
  // Highlight borders slightly more if the user is frustrated
  const activeBorderColor = agentState?.ui?.highlightCTAs 
    ? (isDarkMode ? "rgba(0, 229, 255, 0.3)" : "rgba(0, 0, 0, 0.2)") 
    : borderColor;

  // 4. TELEMETRY: Track time spent specifically in the editing zone
  const hoverStartTime = useRef<number>(0);

  const handleMouseEnter = useCallback(() => {
    hoverStartTime.current = Date.now();
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (hoverStartTime.current > 0) {
      const timeSpentSeconds = (Date.now() - hoverStartTime.current) / 1000;
      
      // If they spent more than 5 seconds interacting with the timeline zone, log high interest
      if (timeSpentSeconds > 5 && reportBehavior) {
        reportBehavior("timeline_editing_focus", timeSpentSeconds * 0.5, "interest");
      }
      hoverStartTime.current = 0;
    }
  }, [reportBehavior]);

  return (
    <Box 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ 
        display: "flex", 
        flexDirection: "column", 
        flexGrow: 1, 
        // Adaptive styling
        backgroundColor: bgColor,
        transition: "background-color 0.5s ease",
        borderTop: `1px solid ${activeBorderColor}`,
        py: 1
      }}
    >
      <Box 
        sx={{ 
          flexGrow: 1, 
          px: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1
        }}
      >
        <VideoEditorToolbar />
        
        {/* Wrap the Time and Timeline in a subtle adaptive container */}
        <Box 
          sx={{
            backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
            borderRadius: "8px",
            border: `1px solid ${activeBorderColor}`,
            p: 1,
            transition: "all 0.5s ease"
          }}
        >
          <VideoEditorCurrentTime />
          <VideoEditorTimeline />
        </Box>
      </Box>
    </Box>
  );
};

export default VideoEditor;